import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { UserAvatar } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { typeLabel, trFold, SearchClear } from "@/components/shared";
import { toast } from "sonner";
import { Plus, Eye, Search, Copy, Upload, Download, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fmtHours = (h) => {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)} dk`;
  if (h < 48) return `${h.toFixed(1).replace(".", ",")} sa`;
  return `${(h / 24).toFixed(1).replace(".", ",")} gün`;
};

const fmtTL = (v) => `${Math.round(v || 0).toLocaleString("tr-TR")} TL`;

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ account_type: "", company_name: "", website: "", contact_name: "", contact_email: "", contact_phone: "+90" });
  const [categories, setCategories] = useState([]);
  const [catOpen, setCatOpen] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [sort, setSort] = useState({ key: null, dir: "desc" });

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: key === "avg_response_hours" ? "asc" : "desc" }));

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/users/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "taseron_sablon.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Şablon indirilemedi");
    }
  };

  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/users/import", fd);
      setImportResult(data);
      if (data.created > 0) {
        toast.success(`${data.created} tedarikçi eklendi, şifre bağlantıları e-posta ile gönderiliyor`);
        load();
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const addCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/categories", { name });
      setCategories((c) => [...c, data].sort((a, b) => a.name.localeCompare(b.name, "tr")));
      setField("account_type", data.name);
      setNewCat("");
      setCatOpen(false);
      toast.success("Kategori eklendi");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setField = (k, v) => {
    if (k === "contact_phone") {
      let val = v;
      if (!val.startsWith("+90")) val = "+90" + val.replace(/^\+?9?0?/, "");
      val = "+90" + val.slice(3).replace(/\D/g, "").slice(0, 10);
      v = val;
    }
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.account_type) e.account_type = "Hesap tipi seçin";
    if (!form.company_name.trim()) e.company_name = "Firma adı zorunludur";
    if (!form.contact_name.trim()) e.contact_name = "Yetkili kişi adı zorunludur";
    if (!EMAIL_RE.test(form.contact_email)) e.contact_email = "Geçerli bir e-posta adresi girin";
    if (!/^\+90\d{10}$/.test(form.contact_phone)) e.contact_phone = "Telefon +90 ile başlamalı ve 10 basamak olmalıdır";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/users", form);
      toast.success(data.email_sent ? "Hesap oluşturuldu, şifre bağlantısı e-posta ile gönderildi" : "Hesap oluşturuldu (e-posta gönderilemedi, bağlantıyı manuel iletin)");
      setCreatedLink(data.setup_link);
      setForm({ account_type: "", company_name: "", website: "", contact_name: "", contact_email: "", contact_phone: "+90" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const tabMatch = (u, key) =>
    key === "all" ? true
    : key === "active" ? u.is_active !== false && u.password_set
    : key === "pending" ? !u.password_set && u.is_active !== false
    : key === "invited" ? !!u.invited
    : u.is_active === false;

  const filtered = users.filter(
    (u) =>
      tabMatch(u, tab) &&
      (trFold(u.company_name).includes(trFold(search)) ||
        trFold(u.username).includes(trFold(search)) ||
        trFold(u.contact_name).includes(trFold(search)))
  );

  const sorted = sort.key
    ? [...filtered].sort((a, b) => {
        const av = a[sort.key], bv = b[sort.key];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return sort.dir === "asc" ? av - bv : bv - av;
      })
    : filtered;

  const sortIcon = (key) =>
    sort.key !== key ? <ArrowUpDown size={12} className="text-slate-300" />
    : sort.dir === "asc" ? <ArrowUp size={12} className="text-slate-900" />
    : <ArrowDown size={12} className="text-slate-900" />;

  const field = (key, label, props = {}) => (
    <div className="space-y-1.5">
      <Label htmlFor={key}>{label}</Label>
      <Input
        id={key}
        data-testid={`user-form-${key}`}
        value={form[key]}
        onChange={(e) => setField(key, e.target.value)}
        className="rounded-none bg-[#F5F7FA] border-slate-200"
        {...props}
      />
      {errors[key] && (
        <p className="text-red-500 text-sm" data-testid={`user-form-${key}-error`}>
          {errors[key]}
        </p>
      )}
    </div>
  );

  return (
    <div className="space-y-8 fade-in">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Yönetim</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1">Tedarikçiler</h1>
        </div>
        <div className="flex items-center gap-2">
        <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportResult(null); }}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="open-import-button" className="rounded-none border-slate-200 hover:bg-slate-100 gap-2 transition-colors duration-200">
              <Upload size={16} /> Toplu İçe Aktar
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-bold tracking-tight">Toplu Tedarikçi İçe Aktar</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-500">
                Excel (.xlsx) veya CSV dosyası yükleyin. Sütunlar: Firma Adı, Hesap Tipi, Kullanıcı Adı, Yetkili Kişi Adı Soyadı, Firma Web sitesi, Yetkili Kişi E-Postası, Yetkili Kişi Telefonu.
              </p>
              <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-button" className="w-full rounded-none border-slate-200 hover:bg-slate-100 gap-2 transition-colors duration-200">
                <Download size={15} /> Örnek Şablonu İndir (.xlsx)
              </Button>
              <label className="block">
                <input type="file" accept=".xlsx,.csv" onChange={onImportFile} className="hidden" data-testid="import-file-input" disabled={importing} />
                <div className="w-full border border-dashed border-slate-300 bg-[#F5F7FA] hover:border-slate-400 transition-colors duration-200 p-8 flex flex-col items-center gap-2 text-slate-500 cursor-pointer" data-testid="import-dropzone">
                  <Upload size={20} />
                  <span className="text-sm">{importing ? "İçe aktarılıyor..." : "Dosya seçmek için tıklayın (.xlsx / .csv)"}</span>
                </div>
              </label>
              <p className="text-xs text-slate-500">Eklenen her tedarikçiye standart şifre <span className="text-slate-700 font-medium">123456</span> atanır ve şifre oluşturma bağlantısı e-posta ile gönderilir.</p>
              {importResult && (
                <div className="space-y-2" data-testid="import-result">
                  <p className="text-sm text-emerald-600 font-medium">{importResult.created} tedarikçi başarıyla eklendi.</p>
                  {importResult.errors?.length > 0 && (
                    <div className="border border-red-200 bg-red-50 p-3 max-h-40 overflow-y-auto space-y-1">
                      {importResult.errors.map((er, i) => (
                        <p key={i} className="text-xs text-red-600">Satır {er.row}: {er.error}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCreatedLink(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="open-create-user-button" className="rounded-none bg-slate-900 text-white hover:bg-slate-700 font-semibold gap-2 transition-colors duration-200">
              <Plus size={16} /> Hesap Oluştur
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-bold tracking-tight">Yeni Tedarikçi Hesabı</DialogTitle>
            </DialogHeader>
            {createdLink ? (
              <div className="space-y-4" data-testid="created-link-panel">
                <p className="text-sm text-emerald-600">Hesap oluşturuldu. Şifre oluşturma bağlantısı yetkili kişiye e-posta ile gönderildi.</p>
                <div className="flex items-center gap-2 bg-[#F5F7FA] border border-slate-200 p-3">
                  <span className="text-xs text-slate-500 truncate flex-1">{createdLink}</span>
                  <Button variant="outline" size="sm" className="rounded-none border-slate-200 shrink-0" data-testid="copy-setup-link"
                    onClick={() => { navigator.clipboard.writeText(createdLink); toast.success("Bağlantı kopyalandı"); }}>
                    <Copy size={13} />
                  </Button>
                </div>
                <Button className="w-full rounded-none bg-slate-900 text-white hover:bg-slate-700" onClick={() => setCreatedLink(null)} data-testid="create-another-user">
                  Yeni Hesap Oluştur
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Hesap Tipi</Label>
                  <Select value={form.account_type} onValueChange={(v) => { if (!v) return; if (v === "__new__") setCatOpen(true); else { setCatOpen(false); setField("account_type", v); } }}>
                    <SelectTrigger className="rounded-none bg-[#F5F7FA] border-slate-200" data-testid="user-form-account-type">
                      <SelectValue placeholder="Kategori seçin">{form.account_type}</SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-none">
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.name} data-testid={`account-type-option-${c.id}`}>{c.name}</SelectItem>
                      ))}
                      <SelectItem value="__new__" data-testid="account-type-add-new" className="text-emerald-600 focus:text-emerald-600">+ Yeni ekle</SelectItem>
                    </SelectContent>
                  </Select>
                  {catOpen && (
                    <div className="flex gap-2 pt-1" data-testid="new-category-row">
                      <Input
                        data-testid="new-category-input"
                        value={newCat}
                        onChange={(e) => setNewCat(e.target.value)}
                        placeholder="Yeni kategori adı, örn. Elektrik Tedarikçisi"
                        className="rounded-none bg-[#F5F7FA] border-slate-200"
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(); } }}
                        autoFocus
                      />
                      <Button type="button" onClick={addCategory} disabled={!newCat.trim()} data-testid="new-category-submit" className="rounded-none bg-slate-900 text-white hover:bg-slate-700 font-semibold shrink-0 transition-colors duration-200">
                        Ekle
                      </Button>
                    </div>
                  )}
                  {errors.account_type && <p className="text-red-500 text-sm" data-testid="user-form-account_type-error">{errors.account_type}</p>}
                </div>
                {field("company_name", "Firma Adı")}
                {field("website", "Firma Web Sitesi", { placeholder: "örn. www.firma.com (opsiyonel)" })}
                {field("contact_name", "Yetkili Kişi Adı Soyadı")}
                {field("contact_email", "Yetkili Kişi E-postası", { placeholder: "ornek@firma.com" })}
                {field("contact_phone", "Yetkili Kişi Telefonu", { placeholder: "+905XXXXXXXXX" })}
                <p className="text-xs text-slate-500">Kullanıcı adı otomatik olarak yetkili kişinin e-posta adresi olur. Hesap oluşturulduğunda standart şifre <span className="text-slate-600">123456</span> olur ve yetkili kişiye şifre oluşturma bağlantısı e-posta ile gönderilir.</p>
                <Button type="submit" disabled={busy} data-testid="user-form-submit" className="w-full rounded-none bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
                  {busy ? "Oluşturuluyor..." : "Hesabı Oluştur"}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="suppliers-kpi-cards">
        {[
          { label: "Toplam Tedarikçi", value: users.length, tid: "kpi-supplier-total" },
          { label: "İş Alan Tedarikçi", value: users.filter((u) => (u.won_count || 0) > 0).length, tid: "kpi-supplier-winners", cls: "text-emerald-600" },
          { label: "Toplam Kazanılan İş", value: users.reduce((s, u) => s + (u.won_count || 0), 0), tid: "kpi-supplier-won-jobs" },
          { label: "Toplam Tutar (TL)", value: fmtTL(users.reduce((s, u) => s + (u.won_total_try || 0), 0)), tid: "kpi-supplier-total-amount", cls: "text-emerald-700" },
        ].map((c) => (
          <div key={c.tid} className="border border-slate-200 bg-white px-4 py-3.5" data-testid={c.tid}>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{c.label}</div>
            <div className={`font-heading text-2xl font-extrabold tracking-tight mt-0.5 ${c.cls || ""}`}>{c.value}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1" data-testid="users-status-tabs">
          {[
            { key: "all", label: "Tümü" },
            { key: "active", label: "Aktif" },
            { key: "pending", label: "Şifre Bekliyor" },
            { key: "invited", label: "Davet ile" },
            { key: "passive", label: "Pasif" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`users-tab-${t.key}`}
              className={`px-4 py-2 text-sm transition-colors duration-200 ${
                tab === t.key ? "bg-slate-900 text-white font-semibold" : "bg-white border border-slate-200 text-slate-500 hover:text-slate-900"
              }`}
            >
              {t.label} <span className="text-xs ml-1 text-slate-400">({users.filter((u) => tabMatch(u, t.key)).length})</span>
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="users-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Firma, kullanıcı adı veya yetkili ara..."
            className="rounded-none bg-white border-slate-200 pl-9 pr-9 h-[38px]"
          />
          <SearchClear value={search} onClear={() => setSearch("")} testid="users-search-clear" />
        </div>
      </div>

      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 font-medium">Firma</th>
              <th className="px-4 py-3 font-medium">Kullanıcı Adı (E-posta)</th>
              <th className="px-4 py-3 font-medium">Hesap Tipi</th>
              <th className="px-4 py-3 font-medium">Yetkili</th>
              <th className="px-4 py-3 font-medium">
                <button onClick={() => toggleSort("win_rate")} data-testid="sort-win-rate" className="flex items-center gap-1 uppercase tracking-wider hover:text-slate-900 transition-colors duration-200">
                  Kazanma Oranı {sortIcon("win_rate")}
                </button>
              </th>
              <th className="px-4 py-3 font-medium">
                <button onClick={() => toggleSort("won_total_try")} data-testid="sort-won-total" className="flex items-center gap-1 uppercase tracking-wider hover:text-slate-900 transition-colors duration-200">
                  Aldığı İş · Tutar {sortIcon("won_total_try")}
                </button>
              </th>
              <th className="px-4 py-3 font-medium">
                <button onClick={() => toggleSort("avg_response_hours")} data-testid="sort-response-time" className="flex items-center gap-1 uppercase tracking-wider hover:text-slate-900 transition-colors duration-200">
                  Ort. Yanıt {sortIcon("avg_response_hours")}
                </button>
              </th>
              <th className="px-4 py-3 font-medium">Şifre Durumu</th>
              <th className="px-4 py-3 font-medium text-right">Detay</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-slate-500" data-testid="users-empty">
                  {users.length === 0 ? "Henüz tedarikçi yok. İlk tedarikçi hesabını oluşturun." : "Arama sonucu bulunamadı."}
                </td>
              </tr>
            ) : (
              sorted.map((u) => (
                <tr key={u.id} className="border-b border-slate-200 last:border-b-0 hover:bg-white transition-colors duration-200" data-testid={`user-row-${u.username}`}>
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-2">
                      <UserAvatar userId={u.id} avatarId={u.avatar_file_id} name={u.company_name} size={28} />
                      <span>
                        {u.company_name}
                        {u.invited && <Badge variant="outline" className="rounded-none text-[10px] ml-2 bg-blue-500/10 text-blue-600 border-blue-500/20">Davet ile</Badge>}
                        {u.is_active === false && <Badge variant="outline" className="rounded-none text-[10px] ml-2 bg-red-500/10 text-red-500 border-red-500/20">Pasif</Badge>}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{u.username}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="rounded-none text-xs border-slate-300 text-slate-600">
                      {typeLabel(u.account_type)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{u.contact_name}</td>
                  <td className="px-4 py-3" data-testid={`win-rate-${u.username}`}>
                    {u.win_rate == null ? (
                      <span className="text-slate-400">—</span>
                    ) : (
                      <span className="font-semibold">%{u.win_rate} <span className="text-xs font-normal text-slate-400">({u.won_count}/{u.offer_count})</span></span>
                    )}
                  </td>
                  <td className="px-4 py-3" data-testid={`won-total-${u.username}`}>
                    {(u.won_count || 0) > 0 ? (
                      <span className="inline-flex flex-col">
                        <span className="text-xs text-emerald-600 font-semibold">{u.won_count} iş</span>
                        <span className="font-heading font-bold tabular-nums">{fmtTL(u.won_total_try)}</span>
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3" data-testid={`response-time-${u.username}`}>
                    {u.avg_response_hours == null ? (
                      <span className="text-slate-400">—</span>
                    ) : u.is_slow ? (
                      <span className="inline-flex items-center gap-1 text-red-600 font-semibold" title="Ortalama yanıt süresi genel ortalamanın 1,5 katından yavaş" data-testid={`slow-badge-${u.username}`}>
                        <AlertTriangle size={13} /> {fmtHours(u.avg_response_hours)}
                      </span>
                    ) : (
                      <span>{fmtHours(u.avg_response_hours)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={`rounded-none text-xs ${u.password_set ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-yellow-500/10 text-yellow-600 border-yellow-500/20"}`}>
                      {u.password_set ? "Şifre oluşturuldu" : "Bekliyor"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/admin/kullanicilar/${u.id}`} data-testid={`user-detail-link-${u.username}`}>
                      <Button variant="outline" size="sm" className="rounded-none border-slate-200 hover:bg-slate-100 h-8">
                        <Eye size={14} />
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
